import React, { useState } from 'react';
import TableHeader from '../../../BaseComponents/TableHeader';
import TableSkeleton from '../../../BaseComponents/TableSkeleton';
import './PublishedTestResultsTable.scss';
import { useSelector, useDispatch } from 'react-redux';
import TestResultsTableRow from './TestResultsTableRow';
import { RootState } from '../../../../store/store';
import {
  deleteBiomarkerResult,
  getMemberBiomarkersResults
} from '../../../../api/biomarkers';
import {
  changeDeleteTestResultModalVisibility,
  setMemberBiomarkersTestResultsList,
  setTestResultIdToDelete
} from '../../../../store/memberSlice';
import ConfirmModal from '../../../BaseComponents/Modal/ConfirmModal';
import { calculateSummaryStatus, resolveRangeData } from '../../../../utils/biomarkers';
import {
  filterBiomarkersByCategory,
  groupBiomarkersByCategory
} from '../../../../utils/biomarkerCategories';

const headerColumns = [
  { title: 'Appointment date ', key: 'date', minWidth: '200px', flex: 0, sortable: true, apiKey: 'appointment_date' },
  { title: 'Biomarker name', key: 'name', minWidth: '250px', flex: 1, sortable: true, apiKey: 'biomarker_name' },
  { title: 'Type', key: 'type', minWidth: '130px', flex: 0 },
  { title: 'Body system', key: 'body-system', minWidth: '150px', flex: 0, sortable: true, apiKey: 'body_system' },
  { title: 'Value', key: 'value', minWidth: '160px', flex: 0, sortable: true, apiKey: 'value' },
  { title: 'Chart', key: 'chart', minWidth: '168px', flex: 0 },
  { title: 'Summary', key: 'summary', minWidth: '120px', flex: 0, sortable: true, apiKey: 'summary' },
  { title: 'Description', key: 'description', minWidth: '200px', flex: 0, sortable: false, apiKey: 'description' },
  { title: '', key: 'actions', minWidth: '48px', flex: 0 },
];

const PublishedTestResultsTable: React.FC = () => {
  const dispatch = useDispatch();
  const [isDeletingResults, setIsDeletingResults] = useState(false);
  const [isDeleteError, setIsDeleteError] = useState<any>(null);
  const [sortingLoading, setSortingLoading] = useState<boolean>(false);
  const {
    memberBiomarkersTestResultsList,
    memberData,
    isDeleteTestResultsModalOpened,
    testResultIdToDelete,
    biomarkerAppointmentDateFilter,
    biomarkerResultsSearchTerm,
    biomarkerResultsBodySystem
  } = useSelector((state: RootState) => state.member);

  const activeAppointmentDate = biomarkerAppointmentDateFilter || undefined;
  const activeSearch = biomarkerResultsSearchTerm || undefined;
  const [memberBiomarkersResultsList, setmemberBiomarkersResultsList] = useState<any>([]);

  const onDeleteClick = async () => {
    setIsDeletingResults(true);
    setIsDeleteError(false);
    try {
      await deleteBiomarkerResult([testResultIdToDelete]);
      const resp = await getMemberBiomarkersResults(
        memberData?.user_id!,
        activeSearch,
        undefined,
        undefined,
        activeAppointmentDate
      );
      dispatch(setMemberBiomarkersTestResultsList(resp.biomarkers));
      setmemberBiomarkersResultsList([]);
      dispatch(changeDeleteTestResultModalVisibility(false));
      dispatch(setTestResultIdToDelete(null));
    } catch (error) {
      console.error(error);
      setIsDeleteError('Error deleting results');
    } finally {
      setIsDeletingResults(false);
    }
  };

  const onSort = async (columnKey: string, direction: 'asc' | 'desc') => {
    if (memberData?.user_id) {
      try {
        setSortingLoading(true);

        if (columnKey === 'summary') {
          const resultsToSort = memberBiomarkersResultsList.length > 0
            ? [...memberBiomarkersResultsList]
            : [...(memberBiomarkersTestResultsList || [])];

          if (resultsToSort.length === 0) {
            setSortingLoading(false);
            return;
          }

          const sortedResults = resultsToSort.sort((a, b) => {
            try {
              const aRangeData = resolveRangeData(a.biomarker, memberData);
              const bRangeData = resolveRangeData(b.biomarker, memberData);

              const aSummary = calculateSummaryStatus(
                a.biomarker,
                a.result.value,
                aRangeData,
                memberData
              ) || '';

              const bSummary = calculateSummaryStatus(
                b.biomarker,
                b.result.value,
                bRangeData,
                memberData
              ) || '';

              if (!aSummary && !bSummary) return 0;
              if (!aSummary) return 1;
              if (!bSummary) return -1;

              if (direction === 'asc') {
                return aSummary.localeCompare(bSummary);
              } else {
                return bSummary.localeCompare(aSummary);
              }
            } catch (error) {
              console.warn('Error during summary sorting:', error);
              return 0;
            }
          });

          setmemberBiomarkersResultsList(sortedResults);
          setSortingLoading(false);
          return;
        }

        const resp = await getMemberBiomarkersResults(
          memberData.user_id,
          activeSearch ?? '',
          columnKey,
          direction,
          activeAppointmentDate
        );
        if (resp && resp.biomarkers) {
          setmemberBiomarkersResultsList(resp.biomarkers);
        } else {
          throw new Error('Invalid response from API');
        }
      } catch (error) {
        console.warn('API sorting failed, using client-side sorting:', error);
        const resultsToSort = memberBiomarkersResultsList.length > 0
          ? [...memberBiomarkersResultsList]
          : [...(memberBiomarkersTestResultsList || [])];

        if (resultsToSort.length === 0) {
          setSortingLoading(false);
          return;
        }

        const sortedResults = resultsToSort.sort((a, b) => {
          let aValue: any, bValue: any;

          try {
            if (columnKey === 'value') {
              aValue = a.result.value || '';
              bValue = b.result.value || '';

              if (a.biomarker.biomarker_type === 'BINARY') {
                aValue = aValue === 'POSITIVE' ? 1 : 0;
                bValue = bValue === 'POSITIVE' ? 1 : 0;
              } else {
                aValue = parseFloat(aValue) || 0;
                bValue = parseFloat(bValue) || 0;
              }
            } else if (columnKey === 'appointment_date') {
              aValue = new Date(a.result.appointment_date).getTime();
              bValue = new Date(b.result.appointment_date).getTime();
            } else if (columnKey === 'name') {
              aValue = a.biomarker.name.toLowerCase();
              bValue = b.biomarker.name.toLowerCase();
            } else if (columnKey === 'body_system') {
              aValue = a.biomarker.body_systems?.[0]?.title?.toLowerCase() || '';
              bValue = b.biomarker.body_systems?.[0]?.title?.toLowerCase() || '';
            }

            if (direction === 'asc') {
              return aValue > bValue ? 1 : -1;
            } else {
              return aValue < bValue ? 1 : -1;
            }
          } catch (error) {
            console.warn('Error during client-side sorting:', error);
            return 0;
          }
        });
        setmemberBiomarkersResultsList(sortedResults);
      } finally {
        setSortingLoading(false);
      }
    }
  };

  const displayMembers = memberBiomarkersResultsList.length > 0
    ? memberBiomarkersResultsList
    : (memberBiomarkersTestResultsList || []);

  const filteredMembers = biomarkerResultsBodySystem
    ? filterBiomarkersByCategory(displayMembers, biomarkerResultsBodySystem)
    : displayMembers;

  const groupedMembers = memberBiomarkersResultsList.length > 0
    ? null
    : groupBiomarkersByCategory(filteredMembers);

  return (
    <div className='scroll-container'>
      <div className='table-container published-test-results-table'>
        <TableHeader headerColumns={headerColumns} gap={'28px'} onSort={onSort} />
        <div className='table-body published-test-results-table-body'>
          {sortingLoading ? (
            <TableSkeleton rows={5} columns={7} />
          ) : groupedMembers && groupedMembers.length > 0 ? (
            groupedMembers.map((group) => (
              <React.Fragment key={group.section.id}>
                <div className='biomarker-category-section-header'>
                  {group.section.title}
                </div>
                {group.results.map((result: any) => (
                  <TestResultsTableRow resultData={result} key={result.result.id} />
                ))}
              </React.Fragment>
            ))
          ) : filteredMembers && filteredMembers.length > 0 ? (
            filteredMembers.map((result: any) => (
              <TestResultsTableRow resultData={result} key={result.result.id} />
            ))
          ) : (
            <div className='no-results'>No biomarker results found</div>
          )}
        </div>
      </div>
      {isDeleteTestResultsModalOpened && (
        <ConfirmModal
          isOpen={isDeleteTestResultsModalOpened}
          onClose={() => {
            dispatch(changeDeleteTestResultModalVisibility(false));
          }}
          onConfirmClick={onDeleteClick}
          title='Delete biomarker results?'
          subtitle='This action will permanently remove the results. Once deleted, it cannot be restored.'
          loading={isDeletingResults}
          error={isDeleteError ? 'Error deleting results' : undefined}
        />
      )}
    </div>
  );
};

export default PublishedTestResultsTable;
